/**
 * FB-1435 — the two things the "Choose options" flyout needs that cannot be
 * expressed as attributes on the panel itself.
 *
 * Everything else about this dialog is declared in Liquid: the panels carry
 * `data-a11y-dialog` to opt into the shared primitive's modal semantics, and
 * `x-effect` engages `trapFocusWhenReady` when their open flag flips. See
 * `snippets/card-product.liquid`, `snippets/card-product-shop-the-look.liquid`
 * and `sections/quick-view.liquid`.
 *
 * ─── 1. Who opened it ────────────────────────────────────────────────────────
 *
 * The desktop panel lives inside `.card-product`, so its close handlers can
 * find the trigger by walking up to the card. The mobile popup does not: it is
 * a single element in `sections/quick-view.liquid`, shared by every card on the
 * page, and `$el.closest('.card-product')` from inside it is `null`. That is
 * finding 81412 (focus not returned to the trigger) on mobile — the close
 * button resolved its opener to nothing and focus fell to `<body>`.
 *
 * Recorded here from one delegated listener rather than by threading the opener
 * through each trigger's `@click`, because there are ten of them across two
 * card snippets and they would all have to keep agreeing.
 *
 * ─── 2. The trip through the mini-cart ───────────────────────────────────────
 *
 * Adding to cart from the flyout opens the mini-cart *over* the still-open
 * flyout. The shared primitive holds one trap at a time, so the cart's
 * `trapFocus` releases the flyout's trap, applies its own, and marks the flyout
 * inert along with the rest of the page — which is correct while the cart is
 * the thing on screen.
 *
 * Closing the cart un-inerts the page and restores focus to the Add-to-cart
 * button (the drawer's own `previouslyFocusedElement`, see
 * `assets/custom-cart-components.js`). What it cannot do is put the flyout's
 * trap back — it has no idea the flyout is there. Without this, the user lands
 * back inside a panel that is visually modal, reads as `role="dialog"`, and
 * lets Tab walk straight out into the page behind it.
 *
 * This module then moves focus on from the Add-to-cart button to the flyout's
 * close button, so focus ends up where it starts every other time the dialog
 * opens — and on the one control in the panel that paints a visible ring for
 * programmatically-moved focus. See the comment at the re-engage below.
 *
 * Deliberately not solved by making the primitive hold a stack of traps. That
 * would change nesting behaviour for the ~18 overlays sharing the store that
 * nobody in this batch has audited, and the one-trap constraint is a recorded
 * decision (`docs/specs/FB-1429.md` §2.6). The handoff is only needed between
 * these two specific surfaces, so it lives with the surface that knows about
 * both.
 */
(() => {
  'use strict';

  /** Panels that opted into the flyout's dialog treatment. */
  const PANEL_SELECTOR = '[data-choose-options-dialog]';

  /**
   * The control that opened the flyout most recently. Not cleared on close:
   * the close handlers read it *while* closing, and a stale value is harmless
   * because it is only ever read to restore focus to a trigger that is still
   * on the page.
   */
  let opener = null;

  /**
   * The flyout that was on screen when the mini-cart took over, waiting to have
   * its trap restored when the cart goes away.
   */
  let interrupted = null;

  const focusStore = () =>
    window.Alpine && typeof window.Alpine.store === 'function'
      ? window.Alpine.store('xFocusElement')
      : null;

  /**
   * Falls back to a connectedness check if the primitive has not published its
   * predicate yet. Both files are `defer`red in document order so this should
   * not happen, but the failure mode of guessing wrong here is a trap engaged
   * on a hidden panel — worth the two lines.
   */
  const isRendered = (el) => {
    const fn = window.FarmacyA11y && window.FarmacyA11y.isRendered;
    if (typeof fn === 'function') return fn(el);
    return !!(el && el.isConnected);
  };

  /**
   * The flyout panel currently on screen.
   *
   * Only one is ever open in practice, but nothing in the theme enforces that —
   * `showOptions` is per-card state and opening card B's flyout does not close
   * card A's. Preferring the panel that contains focus picks the one the user
   * is actually in, which is the one that was interrupted.
   */
  const openPanel = () => {
    const panels = Array.from(document.querySelectorAll(PANEL_SELECTOR));
    if (!panels.length) return null;

    const active = document.activeElement;
    if (active) {
      const focused = panels.find((panel) => panel.contains(active) && isRendered(panel));
      if (focused) return focused;
    }

    return panels.find(isRendered) || null;
  };

  /**
   * Capture phase, so the opener is recorded before the trigger's own Alpine
   * handler runs and starts loading the panel.
   *
   * Identified structurally rather than by class: every trigger carries
   * `data-id` holding the id suffix of the panel it opens, and the panel
   * element exists in the markup whether or not it has ever been opened. A
   * `[data-id]` that resolves to a `#choose-options-*` element is a flyout
   * trigger; anything else in the theme using `data-id` resolves to nothing and
   * is ignored.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-id]');
      if (!trigger || !trigger.dataset.id) return;
      if (!document.getElementById(`choose-options-${trigger.dataset.id}`)) return;

      opener = trigger;
    },
    true
  );

  /**
   * The drawer dispatches this from `toggleVisibility()` *before* it releases
   * its trap and restores focus, so both branches defer a frame and read the
   * settled state rather than racing it.
   */
  document.addEventListener('mini-cart:visibility', (event) => {
    const visible = !!(event.detail && event.detail.visible);

    if (visible) {
      interrupted = openPanel();
      return;
    }

    const panel = interrupted;
    interrupted = null;
    if (!panel) return;

    requestAnimationFrame(() => {
      // The flyout may have been dismissed while the cart was open — by the
      // resize handler, or by a second add-to-cart flow. Nothing to restore.
      if (!panel.isConnected || !isRendered(panel)) return;

      const store = focusStore();
      if (!store || typeof store.trapFocusWhenReady !== 'function') return;

      // Focus goes to the flyout's close button, NOT back to the Add-to-cart
      // button the drawer restored to (dev decision).
      //
      // Returning to Add-to-cart was the first implementation — resume where
      // you left off — but it reads wrong here: the user has just added this
      // product and dismissed the cart, so parking them on "add it again" is
      // the one action they are least likely to want, and the X is where focus
      // starts every other time this dialog opens. Landing there is both the
      // useful next step and the consistent one.
      //
      // It is also the target that can actually be *seen*: the close button
      // carries a plain `:focus` indicator in custom-theme.css.liquid, added
      // because `:focus-visible` suppresses the ring on programmatic focus that
      // follows a pointer interaction — which is exactly this path.
      //
      // Passed explicitly rather than relying on the primitive's
      // first-focusable fallback. That fallback would land on the same button
      // today, but only because the injected markup happens to put it first;
      // here, unlike on open, the content is already in the DOM, so the element
      // can be named directly. A null (markup changed, panel re-rendered)
      // degrades to that fallback.
      store.trapFocusWhenReady(panel, panel.querySelector('.group-close-btn'));
    });
  });

  /**
   * Read from the mobile popup's close handlers in `sections/quick-view.liquid`,
   * which have no card to walk up to.
   */
  window.FarmacyA11y = Object.assign(window.FarmacyA11y || {}, {
    chooseOptions: {
      opener: () => opener
    }
  });
})();
